/* eslint-disable react/no-danger */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Icon, CatcherWristbandHeatMap, Button, hitType, positionName, number, pitchResult, styles, pitchTypeDisplayName, getAbsSuffix } from 'yankees-react-utils';
import { GameReport, useOktaMutation } from '../../../data';
import VideoPlayerButton from '../../../components/VideoPlayerButton';

const StyledHeatmap = styled.td`
    &&& {
        padding-left: 2px;
        padding-right: 2px;
        padding-bottom: 0;
    }
    > div {
        margin: auto;
    }
`;

const AtBatTableRow = ({ pitch: p, isFirstInGroup, gameid, canFlagPitch, isInBats }) => {
    const { t: translate } = useTranslation();
    const queryClient = useQueryClient();
    const queryKey = ['pitch-review', gameid];

    const { mutate: flagPitch, isLoading: isAddingPitch } = useOktaMutation(
        (t) => GameReport.addCatcherReviewPitch(t, { GameID: p.gameid, EventSeq: p.eventseq, PitchSeq: p.pitchseq, CatcherID: p.catcherid }),
        { onSuccess: (r) => queryClient.setQueryData(queryKey, (val) => [...val, r]) },
    );
    const { mutate: removePitch, isLoading: isRemovingPitch } = useOktaMutation(
        (t) => GameReport.deleteCatcherReviewPitch(t, p.review),
        { onSuccess: () => queryClient.setQueryData(queryKey, (val) => val.filter((x) => x !== p.review)) },
    );

    let result;
    if (p.islastpitch) {
        if (!p.baseevent || p.baseevent === 8) {
            const hitTypeAndLocation = `${hitType(p.hittype, true, translate)} ${translate('common.to')} ${positionName(p.fielderposition, translate)}`;
            result = hitTypeAndLocation + (p.exitvelo ? ` (${translate('format.abbr-mph', { value: number(p.exitvelo, 1) })})` : '');
        } else {
            let suffix = '';
            if (p.isabsreviewpitch) {
                suffix = getAbsSuffix(translate, p.isabsreviewpitch, p.isoverturned);
            } else {
                suffix = p.baseevent === 7 && ` (${p.result === 4 ? translate('baseball.looking') : translate('baseball.swinging')})`;
            }
            result = p.baseevent_desc + (suffix || '');
        }
        result = <b>{result}</b>;
    } else {
        result = pitchResult(p.result, true, translate, p.isabsreviewpitch, p.isoverturned);
    }

    let onFlagClick;
    if (canFlagPitch) onFlagClick = p.review ? removePitch : flagPitch;

    return (
        <>
            <tr>
                {isInBats ? (
                    <td>
                        {isFirstInGroup && (
                            <>
                                <VideoPlayerButton gameid={p.gameid} eventseq={p.eventseq} /><br />
                            </>
                        )}
                        <VideoPlayerButton gameid={p.gameid} eventseq={p.eventseq} pitchseq={p.pitchseq} />
                    </td>
                ) : <td />}
                <td>{p.balls}-{p.strikes}</td>
                <td>{pitchTypeDisplayName(p, undefined, undefined, translate)}</td>
                <td>{number(p.velocity, 1)}</td>
                <td>{result}{p.isabsreviewpitch && (
                    <div>
                        {p.abschallengeresult}
                    </div>
                )}
                </td>
                <td>
                    {canFlagPitch
                        ? (
                            <Button variant="link" onClick={onFlagClick} disabled={isAddingPitch || isRemovingPitch}>
                                <Icon icon="flag" color={p.review ? styles.theme.background.astronaut : undefined} />
                            </Button>
                        )
                        : <Icon icon="flag" color={p.review ? styles.theme.background.astronaut : undefined} />}
                </td>
                {p.pitcherRepertoire.map((pt) => {
                    const isPitchType = p.fxtype === pt.pitchtype;
                    const ctwbImg = p.catcherWristbandImg.find((i) => i.PitchType === pt.pitchtype && (i.FxSubType === pt.fxsubtype || (i.FxSubType === null && pt.fxsubtype === 0)) && i.Split === (p.strikes === 2 ? 'TwoStrikes' : 'Early'));
                    const override = p.overrides && p.overrides[pt.pitchtype] && p.overrides[pt.pitchtype][pt.fxsubtype];
                    return (
                        <StyledHeatmap key={pt} rowSpan={(p.review?.Comments) ? 2 : 1}>
                            <CatcherWristbandHeatMap
                                is2K={p.strikes === 2}
                                grade={override?.val}
                                src={ctwbImg ? `data:${ctwbImg.ContentType};base64,${ctwbImg.HeatMapImg}` : undefined}
                                redX={override?.RedX}
                                selected={override?.Selected}
                                otz={override?.OTZ}
                                kFree={override?.KFree}
                                plateX={isPitchType ? p.platex : undefined}
                                plateZ={isPitchType ? p.platez : undefined}
                                size={100}
                            />
                        </StyledHeatmap>
                    );
                })}
            </tr>
            {!!p.review?.Comments && (
                <tr>
                    <td colSpan={6} dangerouslySetInnerHTML={{ __html: p.review.Comments }} />
                </tr>
            )}
        </>
    );
};

AtBatTableRow.propTypes = {
    pitch: PropTypes.shape({
        gameid: PropTypes.number.isRequired,
        eventseq: PropTypes.number.isRequired,
        pitchseq: PropTypes.number.isRequired,
        islastpitch: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]).isRequired,
        result: PropTypes.number.isRequired,
        balls: PropTypes.number.isRequired,
        strikes: PropTypes.number.isRequired,
        fxtype: PropTypes.number.isRequired,
        velocity: PropTypes.number.isRequired,
        baseevent: PropTypes.number,
        baseevent_desc: PropTypes.string, // eslint-disable-line camelcase
        hittype: PropTypes.number,
        fielderposition: PropTypes.number,
        exitvelo: PropTypes.number,
        pitcherRepertoire: PropTypes.arrayOf(PropTypes.number).isRequired,
        catcherWristbandImg: PropTypes.arrayOf(PropTypes.shape({
            PitchType: PropTypes.number.isRequired,
            ContentType: PropTypes.string.isRequired,
            HeatMapImg: PropTypes.string.isRequired,
        })).isRequired,
        review: PropTypes.shape({
            Comments: PropTypes.string,
        }),
    }).isRequired,
    isFirstInGroup: PropTypes.bool.isRequired,
    gameid: PropTypes.number.isRequired,
    canFlagPitch: PropTypes.bool,
    isInBats: PropTypes.bool,

};
AtBatTableRow.defaultProps = {
    canFlagPitch: false,
};

export default AtBatTableRow;
